/**
 * useRecordingSession — App Shell recording hook
 *
 * Uses pre-recorded chunked upload and server-side transcription.
 * Manages the current recording flow.
 *
 * See: docs/features/integrations/deepgram-prerecorded.md
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { fetchPatientData } from "../utils/fetchPatientData";
import { fetchDocumentTypes } from "../utils/fetchDocumentTypes";
import { fetchAudioInputDevices, handleMicChange, volumeMeter } from "../utils/audioUtils";
import { updateUserSettings, getUserSettings, getDocument, updateData, addData, setData } from "../utils/firestoreService";
import { getActiveShareForUser, canPerformAction, getSharedDocumentMetadata } from "../utils/sharingService";
import {
  recordingTimeLimit, preRecordedRollingTranscription,
  chunkDurationRamp, preRecordedChunkDurationMs,
  lowVolumeThreshold, lowVolumeWarningDelay, lowVolumeToastDelay, lowVolumeToastRepeat,
  clippingThreshold, clippingWarningDelay, silenceThreshold, volumePollingInterval,
  minimumTranscriptWordCount,
} from "../globals";
import { assessChunkQuality, getChunkQcWarning, buildChunkQcRecord, assessOverallQuality, buildQualityNote, computeRecordingStatus, QUALITY_FAIR, QUALITY_POOR, QUALITY_EXCELLENT, QUALITY_GOOD } from "../utils/audioQualityUtils";
import { networkMonitor } from "../utils/networkUtils";
import { submitForProcessing } from "../utils/processingService";
import { logAuditEventAsync, AuditActions } from "../utils/auditLogger";
import { showNotification } from "../utils/notificationSystem";
import { useAuth } from "../utils/authContext";
import { SESSION_STATE, extendSession } from "../utils/sessionTimeout";
import { AudioChunkRecorder } from "../utils/audioRecorder";
import { saveChunkToIDB, clearChunksForDocument, getChunksForDocument } from "../utils/audioIndexedDB";
import { uploadAllChunks, requestTranscription, uploadSingleChunk, transcribeSingleChunk, requestFinalAssembly } from "../utils/audioUpload";
import { createEmptySections } from "../utils/documentCreationUtils";
import { v4 as uuidv4 } from "uuid";
import { useActiveJobsContext } from "../contexts/ActiveJobsContext";

/**
 * @param {Object} params
 * @param {string} params.patientId — Current patient ID
 * @param {string} params.userId — Current user ID
 * @param {boolean} params.isStreaming — From StreamingContext
 * @param {Function} params.setIsStreaming — From StreamingContext
 * @param {Function} params.navigate — React Router navigate
 * @param {Function} params.setPatientIdLock — Lock patient during processing
 */
export function useRecordingSession({
  patientId,
  userId,
  isStreaming,
  setIsStreaming,
  navigate,
  setPatientIdLock,
  setIsProcessing,
}) {
  // ── Auth (session timeout) ───────────────────────────────
  const { sessionState, extendSessionOnAction } = useAuth();

  // ── State ────────────────────────────────────────────────
  const [patientData, setPatientData] = useState({});
  const [patientDocuments, setPatientDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [documentId, setDocumentId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [processingFailed, setProcessingFailed] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("Waiting");
  const [retryStatus, setRetryStatus] = useState("");
  const [eta, setEta] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [loadedPatientId, setLoadedPatientId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [audioWarning, setAudioWarning] = useState("");  // Inline warning for ActivityBarHeader
  const [showQualityGate, setShowQualityGate] = useState(false);      // Phase 4: quality gate dialog
  const [qualityAssessment, setQualityAssessment] = useState(null);   // Phase 4: { label, blocked, blockReason, summary }
  const [isStarting, setIsStarting] = useState(false);               // True between confirmStart and isStreaming=true
  const [showCancelConfirm, setShowCancelConfirm] = useState(false); // True when user clicked Cancel and needs to confirm
  const [micJustChanged, setMicJustChanged] = useState(false);       // Brief flash after mic swap

  // Phase 6: Recording Status UI — chunk history for timeline + status pill
  const [chunkStatusHistory, setChunkStatusHistory] = useState([]);   // Array of { qcResult, speakerCount, timestamp }
  const [localSilenceDetected, setLocalSilenceDetected] = useState(false); // True when 10s+ of silence detected
  const [adaptiveSilenceThreshold, setAdaptiveSilenceThreshold] = useState(silenceThreshold); // Per-session threshold tuned from startup levels

  // ── Refs ─────────────────────────────────────────────────
  const documentIdRef = useRef(null);
  const streamSessionIdRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const wakeLockRef = useRef(null);
  const recordingStartRef = useRef(null);
  const recordingTickRef = useRef(null);
  const chunkRecorderRef = useRef(null);
  const isProcessingRef = useRef(false);
  const loadedPatientContextRef = useRef(null); // Updated atomically only after the current patient's fetch completes
  const recordingPatientIdRef = useRef(null); // Pinned at recording start — stable across navigation
  const recordingPatientDataRef = useRef(null); // Pinned patient record — used so the ProcessingPill shows the recording's patient name even if the user navigates to another chart before stopping (Userback 7583225)
  const recordingPatientDocumentsRef = useRef(null); // Pinned history from the same validated patient load as id + demographics
  const pausedAtRef = useRef(null); // Timestamp when pause started
  const micChangedTimerRef = useRef(null); // Auto-clear micJustChanged
  const isPausedRef = useRef(false); // Sync with isPaused state for interval access
  const preparedSettingsRef = useRef({}); // User settings fetched during prep phase

  // Rolling transcription state (Phase 1 chunk architecture)
  const chunkResultsRef = useRef([]);     // Accumulated per-chunk transcription results: { chunkIndex, gcsUri, transcript, wordCount, quality, failed? }
  const chunkGcsUrisRef = useRef([]);     // All GCS URIs in order (for final assembly)
  const pendingOpsRef = useRef([]);       // Promise array for in-flight upload+transcribe operations

  // Per-chunk QC tracking (Phase 2 audio quality gates)
  const chunkQcResultsRef = useRef([]);   // Array of { chunkIndex, durationMs, wordCount, wordsPerMinute, avgConfidence, qcResult, transcribedAt }

  // Phase 4: pending transcript + session ID held while quality gate dialog is shown
  const pendingTranscriptRef = useRef(null);    // { transcript, quality, wordCount, speakerCount, sessId }
  const handoffRef = useRef(null);               // stable ref → handleProcessingHandoff (avoids TDZ)
  const startPreRecordedSessionRef = useRef(null); // stable ref → startPreRecordedSession (defined after confirmStart)

  // Silence detection (moved from ActivityBarHeader for timeline dot integration)
  const silenceStartTimeRef = useRef(null);     // When silence began
  const lastDotTimestampRef = useRef(0);        // When last dot was added (for 1-per-minute limiting)
  const silenceCalibrationStartRef = useRef(null); // Session-relative start time for threshold tuning
  const maxCalibrationVolumeRef = useRef(0);       // Highest observed 0-9 volume in startup window
  const silenceThresholdLockedRef = useRef(false); // Prevent repeated threshold updates per session

  // Active jobs context for pending job tracking in ProcessingPill
  const activeJobsCtx = useActiveJobsContext();
  const activeJobsRef = useRef(activeJobsCtx);
  activeJobsRef.current = activeJobsCtx;

  // Volume monitoring state (Phase 3 audio quality gates)
  const volumeMeterRef = useRef(null);        // { cleanup, getVolumeInfo } from volumeMeter()
  const volumePollRef = useRef(null);         // setInterval ID for volume polling
  const lowVolumeStartRef = useRef(null);     // Timestamp when sustained low volume began
  const clippingStartRef = useRef(null);      // Timestamp when sustained clipping began
  const lastToastTimeRef = useRef(null);      // Timestamp of last escalation toast
  const volumeGoodStreakRef = useRef(0);       // Consecutive good polls (to clear warning)
  const audioQualityMetricsRef = useRef(null); // Cumulative { totalDurationMs, silenceDurationMs, lowVolumeDurationMs, clippingDurationMs, warningsShown }

  // ── Session refresh during active recording ──────────────
  // Long recordings that start near the end of a session would otherwise
  // push the session into GRACE/EXPIRED mid-stream, causing an immediate
  // logout the moment the user stops. While streaming, refresh the session
  // every 10 min (writes a fresh sessionStartTime to Firestore). Bypasses
  // the ACTIVE/WARNING gate in `extendSessionOnAction` because an active
  // recording is an explicit presence signal regardless of elapsed time.
  useEffect(() => {
    if (!isStreaming || !userId) return;
    // Kick one off immediately so a recording started mid-WARNING refreshes
    // without waiting for the first interval.
    extendSession(userId);
    const id = setInterval(() => { extendSession(userId); }, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [isStreaming, userId]);

  // ── Actions ──────────────────────────────────────────────

  const logMicAppliedSettings = useCallback((stream, contextLabel) => {
    try {
      const track = stream?.getAudioTracks?.()[0];
      if (!track) return;
      const settings = track.getSettings ? track.getSettings() : null;
      const capabilities = track.getCapabilities ? track.getCapabilities() : null;
      console.info("[useRecordingSession] Mic track settings", {
        context: contextLabel,
        requested: {
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        applied: settings,
        supports: {
          autoGainControl: capabilities ? Object.prototype.hasOwnProperty.call(capabilities, "autoGainControl") : false,
          noiseSuppression: capabilities ? Object.prototype.hasOwnProperty.call(capabilities, "noiseSuppression") : false,
          echoCancellation: capabilities ? Object.prototype.hasOwnProperty.call(capabilities, "echoCancellation") : false,
        },
      });
    } catch (error) {
      console.warn("[useRecordingSession] Unable to inspect mic settings", error);
    }
  }, []);

  const getReadyPatientContext = useCallback(() => {
    const context = loadedPatientContextRef.current;
    if (
      !context
      || !patientId
      || loadedPatientId !== patientId
      || context.patientId !== patientId
      || context.patientData?.id !== patientId
    ) {
      return null;
    }
    return context;
  }, [loadedPatientId, patientId]);

  /** Prepare recording — acquire mic, fetch settings, then show confirmation */
  const requestStart = useCallback(async () => {
    if (isProcessingRef.current) {
      showNotification("Please wait for the current recording to finish submitting.", "info", 5000);
      return;
    }
    setIsStarting(true);

    if (!getReadyPatientContext()) {
      showNotification("Patient information is still loading. Please try again in a moment.", "warning", 5000);
      setIsStarting(false);
      return;
    }

    // Block new recordings during grace period
    if (sessionState === SESSION_STATE.GRACE || sessionState === SESSION_STATE.GRACE_URGENT || sessionState === SESSION_STATE.EXPIRED) {
      showNotification("Session expired — please save your work and log in again to start a new recording.", "warning", 6000);
      setIsStarting(false);
      return;
    }

    extendSessionOnAction?.();

    // Check share permissions
    if (shareInfo && !canPerformAction(shareInfo, "add")) {
      showNotification("You do not have permission to add documents to this patient", "error");
      setIsStarting(false);
      return;
    }

    // Require a real document type before proceeding. Historically we had
    // a `|| 1` fallback on typeId writes, which silently poisoned Firestore
    // rows with a legacy id that no longer matches any doc type — the v3
    // processing backend then rejects with "Document type not found for
    // docTypeId: 1". Fail fast with a user-facing error instead.
    if (!selectedDocumentTypeId) {
      showNotification("Please select a document type before recording.", "error");
      setIsStarting(false);
      return;
    }

    // Fetch user settings
    try {
      preparedSettingsRef.current = await getUserSettings(userId);
    } catch (error) {
      console.error("[useRecordingSession] Error fetching user settings:", error);
      preparedSettingsRef.current = {};
    }

    // Acquire mic stream
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedDeviceId ? { ideal: selectedDeviceId } : undefined,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      audioStreamRef.current = stream;
      logMicAppliedSettings(stream, "requestStart");
    } catch (error) {
      console.error("[useRecordingSession] Mic access failed:", error);
      setCurrentStatus("Microphone access denied");
      showNotification("Microphone access denied. Check your browser permissions.", "error");
      setIsStarting(false);
      return;
    }

    // Start volume metering so user sees mic is working
    const volumeMeterSession = volumeMeter(stream, setVolumeLevel, null, {
      enableSilenceDetection: true,
      silenceThreshold: adaptiveSilenceThreshold,
    });
    volumeMeterRef.current = volumeMeterSession;

    // Clear previous recording's timeline dots (Phase 6)
    setChunkStatusHistory([]);

    // Ready — show confirmation
    setIsStarting(false);
    setShowConfirmation(true);
  }, [sessionState, extendSessionOnAction, shareInfo, userId, selectedDeviceId, selectedDocumentTypeId, adaptiveSilenceThreshold, logMicAppliedSettings, getReadyPatientContext]);

  /** Dismiss confirmation and clean up prepared mic */
  const cancelConfirmation = useCallback(() => {
    setShowConfirmation(false);
    // Clean up mic stream acquired during prep
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    if (volumeMeterRef.current) {
      volumeMeterRef.current.cleanup?.();
      volumeMeterRef.current = null;
    }
    setVolumeLevel(0);
  }, []);

  /** Start recording (after confirmation — mic already acquired) */
  const confirmStart = useCallback(async () => {
    if (isProcessingRef.current) {
      showNotification("Please wait for the current recording to finish submitting.", "info", 5000);
      return;
    }
    const recordingContext = getReadyPatientContext();
    if (!recordingContext) {
      cancelConfirmation();
      showNotification("The selected patient changed before recording started. Please try again.", "warning", 6000);
      return;
    }

    setShowConfirmation(false);
    setIsStarting(true);

    try {
      await startPreRecordedSessionRef.current(preparedSettingsRef.current, recordingContext);
    } finally {
      setIsStarting(false);
    }
  }, [
    setIsStreaming,
    cancelConfirmation, getReadyPatientContext,
  ]);

  /** Start a pre-recorded session */
  const startPreRecordedSession = useCallback(async (userSettings, recordingContext) => {
    const sessionPatientId = recordingContext?.patientId || patientId;
    const sessionPatientData = recordingContext?.patientData || patientData;
    const sessionPatientDocuments = recordingContext?.patientDocuments || patientDocuments;

    if (!sessionPatientId || sessionPatientData?.id !== sessionPatientId) {
      showNotification("The selected patient is not ready for recording. Please try again.", "error", 6000);
      return;
    }

    const newSessionId = uuidv4();
    streamSessionIdRef.current = newSessionId;
    setSessionId(newSessionId);

    // Create Firestore document first
    const documentType = documentTypes.find((t) => t.id === selectedDocumentTypeId);
    const documentSections = documentType?.sections || null;

    const newDocData = {
      title: documentType?.name || "New Session",
      description: "Pre-recorded Audio.",
      text: "",
      type: "session",
      typeId: selectedDocumentTypeId,
      sourceType: "Recording",
      createdAt: new Date(),
      modifiedAt: new Date(),
      streamSessionId: newSessionId,
      recordingMode: "prerecorded",
      documentDate: new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }),
      startTime: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
      endTime: "",
      ...getSharedDocumentMetadata(userId, shareInfo),
    };

    if (documentSections) {
      const emptySections = createEmptySections(documentSections);
      Object.assign(newDocData, emptySections);
    }

    // Pin id, demographics, and history from the same completed patient load.
    recordingPatientIdRef.current = sessionPatientId;
    recordingPatientDataRef.current = sessionPatientData;
    recordingPatientDocumentsRef.current = sessionPatientDocuments;

    let createdDocId;
    try {
      createdDocId = await addData(`patients/${sessionPatientId}/documents`, newDocData);
      documentIdRef.current = createdDocId;
      setDocumentId(createdDocId);
      logAuditEventAsync(AuditActions.DOCUMENT_CREATE, {
        resourceId: createdDocId,
        patientId: sessionPatientId,
        metadata: {
          typeId: selectedDocumentTypeId,
          typeName: documentType?.name,
          analyticsCategory: documentType?.subcategory || null,
          mode: "prerecorded",
        },
      });
    } catch (error) {
      console.error("[useRecordingSession] Document creation failed:", error);
      return;
    }

    // Get mic stream (use already-acquired stream from requestStart prep, or acquire fresh)
    let stream = audioStreamRef.current;
    if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: selectedDeviceId ? { ideal: selectedDeviceId } : undefined,
            sampleRate: 16000,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
          },
        });
        audioStreamRef.current = stream;
        logMicAppliedSettings(stream, "startPreRecordedSession-fresh");
      } catch (error) {
        console.error("[useRecordingSession] Mic access failed:", error);
        setCurrentStatus("Microphone access denied");
        return;
      }
    }

    // Start volume metering if not already running from prep phase
    if (!volumeMeterRef.current) {
      const volumeMeterSession = volumeMeter(stream, setVolumeLevel, null, {
        enableSilenceDetection: true,
        silenceThreshold: adaptiveSilenceThreshold,
      });
      volumeMeterRef.current = volumeMeterSession;
    }

    // Reset rolling transcription state
    chunkResultsRef.current = [];
    chunkGcsUrisRef.current = [];
    pendingOpsRef.current = [];
    chunkQcResultsRef.current = [];
    setChunkStatusHistory([]);  // Phase 6: Reset status timeline

    // Reset silence detection and dot timing
    silenceStartTimeRef.current = null;
    lastDotTimestampRef.current = 0;
    setLocalSilenceDetected(false);
    setAdaptiveSilenceThreshold(silenceThreshold);
    silenceCalibrationStartRef.current = Date.now();
    maxCalibrationVolumeRef.current = 0;
    silenceThresholdLockedRef.current = false;

    // Reset volume monitoring state (Phase 3)
    lowVolumeStartRef.current = null;
    clippingStartRef.current = null;
    lastToastTimeRef.current = null;
    volumeGoodStreakRef.current = 0;
    setAudioWarning("");
    audioQualityMetricsRef.current = {
      totalDurationMs: 0,
      silenceDurationMs: 0,
      lowVolumeDurationMs: 0,
      clippingDurationMs: 0,
      warningsShown: 0,
    };

    // Start volume polling interval (Phase 3 escalating warnings)
    volumePollRef.current = setInterval(() => {
      // Guard: interval is created at recording start and cleared on stop
      // Skip processing while paused to avoid false warnings
      if (!volumeMeterRef.current || isPausedRef.current) return;
      const info = volumeMeterRef.current.getVolumeInfo();
      const level = Math.min(9, Math.round((info?.currentVolume || 0) / 10));
      const now = Date.now();
      const metrics = audioQualityMetricsRef.current;
      if (metrics) metrics.totalDurationMs += volumePollingInterval;

      // Sync volumeLevel state for silence detection
      setVolumeLevel(level);

      // ── Silence (volume 0) ─────────────────────────
      if (level === 0 && metrics) {
        metrics.silenceDurationMs += volumePollingInterval;
      }

      // ── Low volume detection ───────────────────────
      // After Deepgram confirms good transcription (≥1 chunk with QC "ok"),
      // volume warnings are suppressed entirely. Deepgram's transcription is
      // ground truth — if it's working, the volume heuristic is wrong.
      // Common with USB headsets and external interfaces that read level 0-2
      // but produce clear transcription. Metrics still tracked for post-recording.
      const goodChunkCount = (chunkQcResultsRef.current || []).filter(c => c && c.qcResult === "ok").length;
      const deepgramConfirmedGood = goodChunkCount >= 1;

      if (level <= lowVolumeThreshold) {
        if (metrics && level > 0) metrics.lowVolumeDurationMs += volumePollingInterval;

        // Skip all warning UI when Deepgram confirms good transcription
        if (deepgramConfirmedGood) {
          // Keep low-volume timer reset so warnings don't fire if we later
          // get a bad chunk (which flips deepgramConfirmedGood off... no it stays,
          // but the chunk QC warning will surface via the status pill instead).
          lowVolumeStartRef.current = null;
          volumeGoodStreakRef.current = 0;
        } else {
          volumeGoodStreakRef.current = 0;

          if (!lowVolumeStartRef.current) {
            lowVolumeStartRef.current = now;
          }

          const elapsed = now - lowVolumeStartRef.current;

          // Tier A: Inline warning after delay
          if (elapsed >= lowVolumeWarningDelay) {
            const msg = level === 0
              ? "No audio detected \u2014 check your microphone"
              : "Low audio \u2014 check microphone placement";
            setAudioWarning(msg);
            // Add warning dot to timeline (if 60s since last dot)
            if (now - lastDotTimestampRef.current >= 60000) {
              lastDotTimestampRef.current = now;
              setChunkStatusHistory(prev => {
                if (prev.length >= 120) return prev; // MAX_TIMELINE_DOTS
                return [...prev, { qcResult: level === 0 ? "very_low" : "low", speakerCount: 0, timestamp: now }];
              });
            }
          }

          // Tier B: Toast escalation
          if (elapsed >= lowVolumeToastDelay) {
            const shouldToast = !lastToastTimeRef.current
              || (now - lastToastTimeRef.current >= lowVolumeToastRepeat);
            if (shouldToast) {
              showNotification(
                level === 0
                  ? "No audio detected. Check that your microphone is connected and positioned correctly."
                  : "Your microphone may not be picking up speech. Check that it\u2019s positioned close to you.",
                "warning", 8000, { key: "volume-low" }
              );
              lastToastTimeRef.current = now;
              if (metrics) metrics.warningsShown++;
            }
          }
        }
      } else {
        // Volume returned to acceptable range — reset the low-volume timer
        // immediately so natural speech pauses don't accumulate toward the
        // 30-second warning threshold. Only the warning *text* clearing
        // requires 5 consecutive good polls (prevents flicker).
        if (lowVolumeStartRef.current) {
          lowVolumeStartRef.current = null;
        }
        volumeGoodStreakRef.current++;
        // Clear warning text after 5 consecutive good polls (~10s)
        if (volumeGoodStreakRef.current >= 5) {
          setAudioWarning((prev) => prev.includes("audio") || prev.includes("Low") ? "" : prev);
        }
      }

      // ── Clipping detection ─────────────────────────
      if (level >= clippingThreshold) {
        if (metrics) metrics.clippingDurationMs += volumePollingInterval;
        if (!clippingStartRef.current) {
          clippingStartRef.current = now;
        }
        if (now - clippingStartRef.current >= clippingWarningDelay) {
          setAudioWarning("Audio is clipping \u2014 try moving further from the microphone");
          // Add warning dot to timeline (if 60s since last dot)
          if (now - lastDotTimestampRef.current >= 60000) {
            lastDotTimestampRef.current = now;
            setChunkStatusHistory(prev => {
              if (prev.length >= 120) return prev; // MAX_TIMELINE_DOTS
              return [...prev, { qcResult: "low", speakerCount: 0, timestamp: now }];
            });
          }
        }
      } else if (clippingStartRef.current) {
        clippingStartRef.current = null;
        setAudioWarning((prev) => prev.includes("clipping") ? "" : prev);
      }
    }, volumePollingInterval);

    // Start chunked recording
    const recorder = new AudioChunkRecorder(stream, {
      onChunkReady: ({ blob, index, durationMs }) => {
        if (documentIdRef.current !== createdDocId) return;
        // Always save to IndexedDB as backup
        saveChunkToIDB(createdDocId, index, blob);

        // Rolling upload + transcription (when enabled)
        if (preRecordedRollingTranscription) {
          const op = (async () => {
            try {
              // Upload chunk to GCS
              const { gcsUri } = await uploadSingleChunk({ blob, index }, createdDocId);
              if (documentIdRef.current !== createdDocId) return;
              chunkGcsUrisRef.current[index] = gcsUri;

              // Transcribe the single chunk (use pinned patientId)
              const result = await transcribeSingleChunk({
                documentId: createdDocId,
                patientId: sessionPatientId,
                chunkUri: gcsUri,
                chunkIndex: index,
              });

              if (documentIdRef.current !== createdDocId) return;
              chunkResultsRef.current[index] = {
                chunkIndex: index,
                gcsUri,
                transcript: result.transcript,
                wordCount: result.wordCount,
                quality: result.quality,
              };

              // Per-chunk QC (Phase 2 audio quality gates)
              // Final chunks and manual rotations can be much shorter than the
              // schedule. Use captured time, excluding pauses, for speech rate.
              const chunkDurationMs = Number.isFinite(durationMs) && durationMs > 0
                ? durationMs
                : (preRecordedRollingTranscription
                  ? chunkDurationRamp[Math.min(index, chunkDurationRamp.length - 1)]
                  : preRecordedChunkDurationMs);
              const { qcResult, wordsPerMinute } = assessChunkQuality({
                wordCount: result.wordCount,
                chunkDurationMs,
              });

              const qcRecord = buildChunkQcRecord({
                chunkIndex: index,
                chunkDurationMs,
                wordCount: result.wordCount,
                quality: result.quality,
                qcResult,
                wordsPerMinute,
              });
              chunkQcResultsRef.current[index] = qcRecord;

              // Phase 6: Update chunk status history for UI
              // Only add dot if 60s since last dot (1-per-minute max) and under cap
              const now = Date.now();
              if (now - lastDotTimestampRef.current >= 60000) { // DOT_MIN_INTERVAL
                lastDotTimestampRef.current = now;
                const speakerCount = result.speakerCount || 1;
                setChunkStatusHistory(prev => {
                  if (prev.length >= 120) return prev; // MAX_TIMELINE_DOTS
                  // Check for one-sided: if this chunk + previous are both single-speaker
                  const isOneSided = speakerCount === 1 && prev.length > 0 && prev[prev.length - 1].speakerCount === 1;
                  const effectiveQcResult = isOneSided ? "one_sided" : qcResult;
                  return [...prev, {
                    qcResult: effectiveQcResult,
                    speakerCount,
                    timestamp: now,
                  }];
                });
              }

              // Immediately clear any active volume warning on good QC.
              // The polling loop will also suppress future warnings via deepgramConfirmedGood,
              // but this ensures the UI updates right away without waiting for the next poll.
              if (qcResult === "ok") {
                lowVolumeStartRef.current = null;
                setAudioWarning((prev) => prev.includes("audio") || prev.includes("Low") ? "" : prev);
              }

              // Show toast warning for QC failures
              const warning = getChunkQcWarning(qcResult, index);
              if (warning) {
                showNotification(warning.message, warning.type, 8000, {
                  key: `chunk-qc-${qcResult}`,
                });
                console.warn(`[useRecordingSession] Chunk ${index} QC: ${qcResult} (${wordsPerMinute} wpm)`);
              }

              console.log(`[useRecordingSession] Chunk ${index} uploaded + transcribed: ${result.wordCount} words, QC: ${qcResult} (${wordsPerMinute} wpm)`);
            } catch (err) {
              if (documentIdRef.current !== createdDocId) return;
              console.error(`[useRecordingSession] Rolling chunk ${index} failed:`, err.message);
              chunkResultsRef.current[index] = { chunkIndex: index, failed: true, error: err.message };
              // Recording continues — failed chunk is not fatal
            }
          })();

          pendingOpsRef.current.push(op);
        }
      },
      onError: (err) => {
        console.error("[useRecordingSession] Recorder error:", err);
        showNotification("Recording error: " + err.message, "error", 5000);
      },
      onChunkSkipped: ({ index, size }) => {
        console.warn(`[useRecordingSession] Audio chunk ${index} was skipped (${size} bytes)`);
        setAudioWarning("Audio capture issue detected — check your microphone");
        showNotification(
          "We couldn't capture usable audio for part of this recording. Check your microphone; recording is still running.",
          "warning",
          8000,
          { key: "unusable-audio-chunk" }
        );
      },
    });

    recorder.start();
    chunkRecorderRef.current = recorder;

    // Store cleanup interface on mediaRecorderRef for timer/wake lock compat
    mediaRecorderRef.current = {
      isPreRecorded: true,
      volumeMeterSession: volumeMeterRef.current,
      disconnect: () => {
        if (volumeMeterRef.current?.cleanup) volumeMeterRef.current.cleanup();
        recorder.cancel();
      },
      pauseRecording: () => recorder.pause(),
      resumeRecording: () => recorder.resume(),
      isPaused: () => recorder.isPaused,
    };

    setIsStreaming(true);
    window._streamingStartTime = Date.now();
    logAuditEventAsync(AuditActions.STREAMING_START, {
      resourceId: createdDocId,
      patientId: sessionPatientId,
      metadata: { sessionId: newSessionId, mode: "prerecorded" },
    });
  }, [patientId, patientData, patientDocuments, selectedDeviceId, selectedDocumentTypeId, documentTypes, shareInfo, setIsStreaming]);
  startPreRecordedSessionRef.current = startPreRecordedSession;

  // Intentional discards must not leave an active empty draft. Invalidate the
  // session synchronously so late callbacks cannot affect a subsequent one.
  const archiveDiscardedRecording = useCallback(async (reason) => {
    const docId = documentIdRef.current;
    const pid = recordingPatientIdRef.current;
    documentIdRef.current = null;
    window._streamingStartTime = null;
    if (!docId || !pid) return;
    activeJobsRef.current.removePendingJob(docId);
    try {
      await updateData(`patients/${pid}/documents`, docId, {
        status: "cancelled", isArchived: true, discardReason: reason, modifiedAt: new Date(),
      });
      logAuditEventAsync(AuditActions.DOCUMENT_ARCHIVE, {
        resourceType: "document", resourceId: docId, patientId: pid,
        metadata: { reason, mode: "prerecorded" },
      });
    } catch (error) {
      console.error("[useRecordingSession] Failed to archive discarded recording:", error);
      showNotification("Recording stopped, but its draft could not be archived. Please review the patient's documents.", "warning", 8000);
    }
  }, []);

  const finishEmptyRecording = useCallback(async () => {
    const draftArchived = archiveDiscardedRecording("no_speech_detected");
    setCurrentStatus("No speech detected");
    setProcessingFailed(false);
    isProcessingRef.current = false;
    if (setIsProcessing) setIsProcessing(false);
    showNotification("No speech was detected. Check your microphone and try recording again.", "warning", 8000);
    await draftArchived;
  }, [archiveDiscardedRecording, setIsProcessing]);

  /** Stop recording and hand off to processing */
  const stop = useCallback(async () => {
    if (isProcessingRef.current) {
      console.log("[useRecordingSession] Already processing, ignoring duplicate stop");
      return;
    }

    isProcessingRef.current = true;

    // Log duration
    const recordingEndedAtMs = Date.now();
    const recordingStartedAtMs = window._streamingStartTime || null;
    const durationMs = recordingStartedAtMs ? recordingEndedAtMs - recordingStartedAtMs : 0;
    logAuditEventAsync(AuditActions.STREAMING_STOP, {
      resourceId: documentIdRef.current,
      patientId,
      metadata: { durationMs, sessionId: streamSessionIdRef.current, outcome: durationMs < 15000 ? "quick-stop" : "completed", mode: "prerecorded" },
    });
    window._streamingStartTime = null;

    // Quick stop (< 15s): silently discard — no quality gate or notifications
    if (durationMs < 15000) {
      const discardedDocId = documentIdRef.current;
      const draftArchived = archiveDiscardedRecording("quick_stop");
      if (chunkRecorderRef.current) {
        chunkRecorderRef.current.cancel();
        chunkRecorderRef.current = null;
        if (discardedDocId) {
          await clearChunksForDocument(discardedDocId);
        }
        chunkResultsRef.current = [];
        chunkGcsUrisRef.current = [];
        pendingOpsRef.current = [];
        chunkQcResultsRef.current = [];
        setChunkStatusHistory([]);  // Phase 6: Reset status timeline
      }
      cleanupMediaRefs();
      setIsStreaming(false);
      documentIdRef.current = null;
      recordingPatientIdRef.current = null;
      recordingPatientDataRef.current = null;
      recordingPatientDocumentsRef.current = null;
      setDocumentId(null);
      isProcessingRef.current = false;
      await draftArchived;
      return;
    }

    // Stop capture immediately; network persistence must not extend the audio.
    setCurrentStatus("Stopping recording...");
    const stoppedChunksPromise = chunkRecorderRef.current?.stop();
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }
    setIsStreaming(false);

    // Show pending job in Processing Pill immediately (for non-quick stops)
    const { addPendingJob } = activeJobsRef.current;
    if (documentIdRef.current && recordingPatientIdRef.current) {
      // Pinned patient name so the pill shows the recording's patient even if
      // the user navigated to another chart before stopping (Userback 7583225).
      const pd = recordingPatientDataRef.current || patientData || {};
      const pinnedName = `${pd.firstName || ""} ${pd.lastName || ""}`.trim();
      addPendingJob(documentIdRef.current, 'Recording', recordingPatientIdRef.current, pinnedName);
    }

    // Persist the displayed times with the canonical interval in one awaited
    // write. A debounced end-time write can be replaced by a fast status update.
    // This intentionally includes paused time; the pause-aware UI timer is a
    // separate presentation value and must not be used for Current Context.
    if (recordingStartedAtMs && documentIdRef.current && recordingPatientIdRef.current) {
      try {
        await updateData(
          `patients/${recordingPatientIdRef.current}/documents`,
          documentIdRef.current,
          {
            startTime: new Date(recordingStartedAtMs).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
            endTime: new Date(recordingEndedAtMs).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
            sessionStartedAt: new Date(recordingStartedAtMs),
            sessionEndedAt: new Date(recordingEndedAtMs),
            sessionDurationMs: durationMs,
            modifiedAt: new Date(),
          }
        );
      } catch (error) {
        // Processing can continue; Current Context will render an empty duration
        // when canonical timing could not be persisted.
        console.error("[useRecordingSession] Failed to persist session timing:", error);
        showNotification("Session times could not be saved. Please review the start and end times in the document.", "warning", 8000);
      }
    }

    // Reset cancel confirm state
    setShowCancelConfirm(false);

    {
      // Upload chunks → transcribe → submit.
      if (!chunkRecorderRef.current) {
        setIsStreaming(false);
        isProcessingRef.current = false;
        return;
      }

      setCurrentStatus("Stopping recording...");
      const chunks = await stoppedChunksPromise;

      // Stop volume polling immediately so stale data doesn't trigger warnings
      if (volumePollRef.current) {
        clearInterval(volumePollRef.current);
        volumePollRef.current = null;
      }
      setAudioWarning("");

      // Cleanup audio stream and volume meter
      if (volumeMeterRef.current?.cleanup) {
        volumeMeterRef.current.cleanup();
      }
      volumeMeterRef.current = null;
      if (mediaRecorderRef.current?.volumeMeterSession?.cleanup) {
        mediaRecorderRef.current.volumeMeterSession.cleanup();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }

      setIsStreaming(false);
      chunkRecorderRef.current = null;

      if (!chunks || chunks.length === 0) {
        await finishEmptyRecording();
        return;
      }

      try {
        if (preRecordedRollingTranscription) {
          // ── Rolling transcription path ──
          // stop() fires onChunkReady for the final chunk before resolving.
          // Its upload may still be in flight, so an absent URI is not evidence
          // that it needs a second upload/transcription (and provider charge).
          setCurrentStatus("Waiting for chunk processing...");
          await Promise.allSettled(pendingOpsRef.current);

          // Retain a fallback for a final chunk not uploaded by the callback.
          const finalChunk = chunks[chunks.length - 1];
          const alreadyUploaded = chunkGcsUrisRef.current[finalChunk.index];

          if (!alreadyUploaded) {
            setCurrentStatus("Uploading final chunk...");
            try {
              const { gcsUri } = await uploadSingleChunk(finalChunk, documentIdRef.current);
              chunkGcsUrisRef.current[finalChunk.index] = gcsUri;

              setCurrentStatus("Transcribing final chunk...");
              const result = await transcribeSingleChunk({
                documentId: documentIdRef.current,
                patientId: recordingPatientIdRef.current,
                chunkUri: gcsUri,
                chunkIndex: finalChunk.index,
              });

              chunkResultsRef.current[finalChunk.index] = {
                chunkIndex: finalChunk.index,
                gcsUri,
                transcript: result.transcript,
                wordCount: result.wordCount,
                quality: result.quality,
              };
            } catch (err) {
              console.error("[useRecordingSession] Final chunk upload/transcribe failed:", err);
              chunkResultsRef.current[finalChunk.index] = { chunkIndex: finalChunk.index, failed: true, error: err.message };
            }
          }

          // Check for failed chunks and attempt recovery from IndexedDB
          const failedIndices = [];
          for (let i = 0; i < chunks.length; i++) {
            const result = chunkResultsRef.current[i];
            if (!result || result.failed) {
              failedIndices.push(i);
            }
          }

          if (failedIndices.length > 0) {
            console.warn(`[useRecordingSession] ${failedIndices.length} chunks failed during rolling transcription, attempting recovery`);
            setCurrentStatus(`Recovering ${failedIndices.length} failed chunk(s)...`);

            // Get chunks from IndexedDB
            const idbChunks = await getChunksForDocument(documentIdRef.current);
            const idbByIndex = new Map(idbChunks.map((c) => [c.chunkIndex, c]));

            for (const idx of failedIndices) {
              const idbChunk = idbByIndex.get(idx);
              if (!idbChunk) {
                console.error(`[useRecordingSession] Chunk ${idx} not found in IndexedDB, cannot recover`);
                continue;
              }

              try {
                setCurrentStatus(`Retrying chunk ${idx + 1}/${chunks.length}...`);
                const { gcsUri } = await uploadSingleChunk({ blob: idbChunk.blob, index: idx }, documentIdRef.current);
                chunkGcsUrisRef.current[idx] = gcsUri;

                const result = await transcribeSingleChunk({
                  documentId: documentIdRef.current,
                  patientId: recordingPatientIdRef.current,
                  chunkUri: gcsUri,
                  chunkIndex: idx,
                });

                chunkResultsRef.current[idx] = {
                  chunkIndex: idx,
                  gcsUri,
                  transcript: result.transcript,
                  wordCount: result.wordCount,
                  quality: result.quality,
                  recovered: true,
                };
                console.log(`[useRecordingSession] Chunk ${idx} recovered successfully: ${result.wordCount} words`);
              } catch (err) {
                console.error(`[useRecordingSession] Chunk ${idx} recovery failed:`, err.message);
                // Still failed — will be missing from final assembly
              }
            }
          }

          // Collect all GCS URIs (filter out any undefined gaps from failed uploads)
          const allGcsUris = chunkGcsUrisRef.current.filter(Boolean);
          const totalChunks = chunks.length;

          if (allGcsUris.length === 0) {
            // All chunks failed — fall back to batch upload
            console.warn("[useRecordingSession] All rolling uploads failed, falling back to batch upload");
            setCurrentStatus("Uploading audio...");
            const gcsUris = await uploadAllChunks(chunks, documentIdRef.current, (done, total) => {
              setCurrentStatus(`Uploading chunk ${done}/${total}...`);
            });

            setCurrentStatus("Transcribing audio...");
            const result = await requestTranscription({
              documentId: documentIdRef.current,
              patientId: recordingPatientIdRef.current,
              chunkUris: gcsUris,
              streamSessionId: streamSessionIdRef.current,
            });

            await clearChunksForDocument(documentIdRef.current);

            if (result.transcript) {
              await runQualityGate(result, streamSessionIdRef.current);
            } else {
              await finishEmptyRecording();
            }
          } else {
            // Normal rolling path — request final assembly (server-side concat)
            setCurrentStatus("Assembling transcript...");
            const result = await requestFinalAssembly({
              documentId: documentIdRef.current,
              patientId: recordingPatientIdRef.current,
              chunkUris: allGcsUris,
              streamSessionId: streamSessionIdRef.current,
              totalChunks,
            });

            // Only clear IndexedDB after successful assembly
            await clearChunksForDocument(documentIdRef.current);

            if (result.transcript) {
              await runQualityGate(result, streamSessionIdRef.current);
            } else {
              await finishEmptyRecording();
            }
          }

          // Clear rolling state
          chunkResultsRef.current = [];
          chunkGcsUrisRef.current = [];
          pendingOpsRef.current = [];

        } else {
          // ── Legacy batch path (kill switch: preRecordedRollingTranscription = false) ──
          setCurrentStatus("Uploading audio...");
          const gcsUris = await uploadAllChunks(chunks, documentIdRef.current, (done, total) => {
            setCurrentStatus(`Uploading chunk ${done}/${total}...`);
          });

          setCurrentStatus("Transcribing audio...");
          const result = await requestTranscription({
            documentId: documentIdRef.current,
            patientId: recordingPatientIdRef.current,
            chunkUris: gcsUris,
            streamSessionId: streamSessionIdRef.current,
          });

          // Keep the local recovery copy until the server has saved a transcript.
          await clearChunksForDocument(documentIdRef.current);

          if (result.transcript) {
            await runQualityGate(result, streamSessionIdRef.current);
          } else {
            await finishEmptyRecording();
          }
        }
      } catch (error) {
        console.error("[useRecordingSession] Pre-recorded pipeline error:", error);
        activeJobsRef.current.removePendingJob(documentIdRef.current);
        if (setIsProcessing) setIsProcessing(false);
        showNotification("Recording upload or transcription failed. Please review the recording before trying again.", "error", 8000);
        setCurrentStatus("Upload/transcription failed");
        setProcessingFailed(true);
        isProcessingRef.current = false;
      }
    }
  }, [patientId, setIsStreaming, archiveDiscardedRecording, finishEmptyRecording, setIsProcessing]);

  /** Request cancel (show dialog) */
  const requestCancel = useCallback(() => {
    setShowCancelDialog(true);
  }, []);

  /** Confirm cancel */
  const confirmCancel = useCallback(async () => {
    setShowCancelDialog(false);

    const durationMs = window._streamingStartTime ? Date.now() - window._streamingStartTime : 0;
    logAuditEventAsync(AuditActions.STREAMING_STOP, {
      resourceId: documentIdRef.current,
      patientId,
      metadata: { durationMs, sessionId: streamSessionIdRef.current, outcome: "cancelled", mode: "prerecorded" },
    });
    window._streamingStartTime = null;
    const discardedDocId = documentIdRef.current;
    const draftArchived = archiveDiscardedRecording("cancelled_by_user");

    if (chunkRecorderRef.current) {
      chunkRecorderRef.current.cancel();
      chunkRecorderRef.current = null;
      if (discardedDocId) {
        await clearChunksForDocument(discardedDocId);
      }
      // Clear rolling transcription state
      chunkResultsRef.current = [];
      chunkGcsUrisRef.current = [];
      pendingOpsRef.current = [];
      chunkQcResultsRef.current = [];
      setChunkStatusHistory([]);  // Phase 6: Reset status timeline
    }

    cleanupMediaRefs();
    setIsStreaming(false);
    documentIdRef.current = null;
    recordingPatientIdRef.current = null;
    recordingPatientDataRef.current = null;
    recordingPatientDocumentsRef.current = null;
    setDocumentId(null);
    isProcessingRef.current = false;
    await draftArchived;
  }, [patientId, setIsStreaming, archiveDiscardedRecording]);

  /** Dismiss cancel dialog without cancelling */
  const dismissCancelDialog = useCallback(() => {
    setShowCancelDialog(false);
  }, []);

  /**
   * Phase 4: Quality gate — intercepts between transcript assembly and processing.
   * Runs aggregate quality assessment. Auto-proceeds for excellent/good; shows dialog for fair/poor.
   * @param {{ transcript: string, quality?: Object, wordCount?: number, speakerCount?: number }} result
   * @param {string} sessId
   */
  const runQualityGate = useCallback(async (result, sessId) => {
    const { transcript, quality: transcriptQuality, wordCount = 0, speakerCount = 0 } = result;

    const assessment = assessOverallQuality({
      transcriptQuality: transcriptQuality || {},
      wordCount,
      speakerCount,
      audioMetrics: audioQualityMetricsRef.current,
      chunkQcResults: chunkQcResultsRef.current,
    });

    console.log(`[useRecordingSession] Quality assessment: ${assessment.label}`, assessment.summary);

    if (assessment.label === QUALITY_EXCELLENT || assessment.label === QUALITY_GOOD) {
      // Auto-proceed
      await handoffRef.current(transcript, sessId, assessment);
    } else {
      // Show quality gate dialog — pause processing until user decides
      pendingTranscriptRef.current = { transcript, quality: transcriptQuality, wordCount, speakerCount, sessId };
      setQualityAssessment(assessment);
      setShowQualityGate(true);
      setCurrentStatus("Review recording quality");
    }
  }, []);

  /** Phase 4: User clicks "Process Anyway" in quality gate dialog */
  const processAnyway = useCallback(async () => {
    // Set the top-bar indicator eagerly so there's no visible gap between
    // the dialog closing and the Firestore onSnapshot firing (~200-500ms).
    // Card: 2026-04-20-processing-indicators-disappear-after-audio-warning.md
    setCurrentStatus("Submitting for processing...");
    if (setIsProcessing) setIsProcessing(true);
    setShowQualityGate(false);
    const pending = pendingTranscriptRef.current;
    if (!pending) return;

    const assessment = qualityAssessment;
    if (assessment) {
      assessment.userOverride = true; // Mark that user chose to proceed despite quality warning
    }

    pendingTranscriptRef.current = null;
    await handoffRef.current(pending.transcript, pending.sessId, assessment);
  }, [qualityAssessment, setIsProcessing]);

  /** Phase 4: User clicks "Discard" in quality gate dialog */
  const discardRecording = useCallback(async () => {
    setShowQualityGate(false);
    setQualityAssessment(null);
    pendingTranscriptRef.current = null;

    logAuditEventAsync(AuditActions.STREAMING_STOP, {
      resourceId: documentIdRef.current,
      patientId,
      metadata: { sessionId: streamSessionIdRef.current, outcome: "discarded_quality_gate", mode: "prerecorded" },
    });

    const draftArchived = archiveDiscardedRecording("discarded_quality_gate");
    cleanupMediaRefs();
    setIsStreaming(false);
    setCurrentStatus("Recording discarded");
    documentIdRef.current = null;
    recordingPatientIdRef.current = null;
    recordingPatientDataRef.current = null;
    recordingPatientDocumentsRef.current = null;
    setDocumentId(null);
    isProcessingRef.current = false;
    await draftArchived;
  }, [patientId, setIsStreaming, archiveDiscardedRecording]);

  /** Retry processing for a failed document */
  const retryProcessing = useCallback(async () => {
    setProcessingFailed(false);
    isProcessingRef.current = true;
    setRetryStatus("");

    const docId = documentIdRef.current;
    if (!docId) {
      console.error("[useRecordingSession] No document ID for retry");
      setProcessingFailed(true);
      isProcessingRef.current = false;
      return;
    }

    // Re-submit — the document already has transcript in Firestore
    setCurrentStatus("Retrying processing...");
    // Trigger a re-process by updating status back to processing
    try {
      const pid = recordingPatientIdRef.current || patientId;
      await updateData(`patients/${pid}/documents`, docId, {
        status: "processing",
        modifiedAt: new Date(),
      });
    } catch (error) {
      console.error("[useRecordingSession] Retry failed:", error);
      setProcessingFailed(true);
      isProcessingRef.current = false;
    }
  }, [patientId]);

  /** Document type selection handler */
  const selectDocumentType = useCallback((e) => {
    const selected = documentTypes.find((type) => type.name === e.target.value);
    if (selected) {
      setSelectedDocumentType(selected.name);
      setSelectedDocumentTypeId(selected.id);
      updateUserSettings(userId, { documentType: selected.id });
      if (selected.categoryId) setSelectedCategoryId(selected.categoryId);
      if (selected.eta) setEta(selected.eta);

      // Mid-recording: update the live Firestore document's typeId and title
      if (isStreaming && documentIdRef.current && (recordingPatientIdRef.current || patientId)) {
        const pid = recordingPatientIdRef.current || patientId;
        updateData(`patients/${pid}/documents`, documentIdRef.current, {
          typeId: selected.id,
          title: selected.name,
          modifiedAt: new Date(),
        });
      }
    }
  }, [documentTypes, userId, isStreaming, patientId]);

  /** Device selection handler */
  const selectDevice = useCallback((deviceId) => {
    if (deviceId === selectedDeviceId) return;
    handleMicChange(deviceId, setSelectedDeviceId, updateUserSettings, audioDevices, userId);
    setMicJustChanged(true);
    if (micChangedTimerRef.current) clearTimeout(micChangedTimerRef.current);
    micChangedTimerRef.current = setTimeout(() => setMicJustChanged(false), 6000);
  }, [audioDevices, userId, selectedDeviceId]);

  /** Swap microphone during an active recording without stopping */
  const swapMicDuringRecording = useCallback(async (newDeviceId) => {
    if (!isStreaming || newDeviceId === selectedDeviceId) return;

    // 1. Get new stream with new device
    let newStream;
    try {
      newStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: newDeviceId },
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
    } catch (error) {
      console.error("[useRecordingSession] Mic swap failed:", error);
      showNotification("Could not switch microphone", "error", 3000);
      return;
    }

    // 2. Cleanup old volume meter
    if (mediaRecorderRef.current?.volumeMeterSession?.cleanup) {
      mediaRecorderRef.current.volumeMeterSession.cleanup();
    }

    // 3. Start new volume meter on new stream
    const newVolumeMeterSession = volumeMeter(newStream, setVolumeLevel);

    // Update the recorder stream and rotate the current chunk.
    if (chunkRecorderRef.current) {
      chunkRecorderRef.current.stream = newStream;
      chunkRecorderRef.current.rotateChunk();
    }

    // 5. Stop old stream tracks
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    audioStreamRef.current = newStream;

    // 6. Update volume meter ref
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.volumeMeterSession = newVolumeMeterSession;
    }

    // 7. Update state + persist
    setSelectedDeviceId(newDeviceId);
    updateUserSettings(userId, { microphone: newDeviceId });

    // 8. Flash "Mic Changed" on the chip
    setMicJustChanged(true);
    if (micChangedTimerRef.current) clearTimeout(micChangedTimerRef.current);
    micChangedTimerRef.current = setTimeout(() => setMicJustChanged(false), 6000);

    console.log(`[useRecordingSession] Mic swapped to ${newDeviceId}`);
  }, [isStreaming, selectedDeviceId, userId]);

  /** Pause the active recording. */
  const pauseRecording = useCallback(() => {
    if (!isStreaming || isPaused) return;
    mediaRecorderRef.current?.pauseRecording?.();
    setIsPaused(true);
    isPausedRef.current = true;
    pausedAtRef.current = Date.now();
    console.log("[useRecordingSession] Recording paused");
  }, [isStreaming, isPaused]);

  /** Resume a paused recording */
  const resumeRecording = useCallback(() => {
    if (!isStreaming || !isPaused) return;
    mediaRecorderRef.current?.resumeRecording?.();
    setIsPaused(false);
    isPausedRef.current = false;
    // The timer effect consumes this timestamp to exclude the paused interval.
    // Clearing it here makes the displayed time jump forward on resume.
    // Reset warning timers to avoid false alarms from paused period
    lowVolumeStartRef.current = null;
    clippingStartRef.current = null;
    silenceStartTimeRef.current = null;
    volumeGoodStreakRef.current = 0;
    setAudioWarning("");
    setLocalSilenceDetected(false);
    console.log("[useRecordingSession] Recording resumed");
  }, [isStreaming, isPaused]);

  // ── Internal helpers ─────────────────────────────────────

  /** Clean up media refs */
  const cleanupMediaRefs = useCallback(() => {
    if (mediaRecorderRef.current) {
      if (typeof mediaRecorderRef.current.disconnect === "function") {
        mediaRecorderRef.current.disconnect();
      }
      mediaRecorderRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    // Clean up volume polling (Phase 3)
    if (volumePollRef.current) {
      clearInterval(volumePollRef.current);
      volumePollRef.current = null;
    }
    volumeMeterRef.current = null;
    setAudioWarning("");
    setIsPaused(false); // Reset pause state on cleanup
    isPausedRef.current = false;
  }, []);

  /** Cancel recording (confirmed) — discard without processing */
  const cancelRecording = useCallback(async () => {
    if (!isStreaming) return;
    setShowCancelConfirm(false);

    console.log("[useRecordingSession] Recording cancelled by user");
    logAuditEventAsync(AuditActions.STREAMING_STOP, {
      resourceId: documentIdRef.current,
      patientId,
      metadata: { sessionId: streamSessionIdRef.current, outcome: "cancelled_by_user", mode: "prerecorded" },
    });

    const discardedDocId = documentIdRef.current;
    const draftArchived = archiveDiscardedRecording("cancelled_by_user");
    // Stop chunk recorder if active (pre-recorded mode)
    if (chunkRecorderRef.current) {
      chunkRecorderRef.current.cancel();
      chunkRecorderRef.current = null;
      if (discardedDocId) {
        await clearChunksForDocument(discardedDocId);
      }
      chunkResultsRef.current = [];
      chunkGcsUrisRef.current = [];
      pendingOpsRef.current = [];
      chunkQcResultsRef.current = [];
      setChunkStatusHistory([]);
    }

    cleanupMediaRefs();
    setIsStreaming(false);
    setCurrentStatus("Recording cancelled");
    documentIdRef.current = null;
    recordingPatientIdRef.current = null;
    recordingPatientDataRef.current = null;
    recordingPatientDocumentsRef.current = null;
    setDocumentId(null);
    isProcessingRef.current = false;
    await draftArchived;
  }, [isStreaming, patientId, setIsStreaming, cleanupMediaRefs, archiveDiscardedRecording]);

  /** Toggle cancel confirmation state */
  const setCancelConfirm = useCallback((show) => {
    setShowCancelConfirm(show);
  }, []);

  /** Process transcript through AI pipeline */
  const handleProcessingHandoff = useCallback(async (transcript, sessId, qualityData = null) => {
    if (!transcript || transcript.trim().length === 0) {
      setCurrentStatus("No audio recorded");
      isProcessingRef.current = false;
      return;
    }

    const networkStatus = networkMonitor.getStatus();
    if (!networkStatus.isOnline) {
      setCurrentStatus("No internet connection");
      setRetryStatus("Please check your internet connection");
      setProcessingFailed(true);
      isProcessingRef.current = false;
      return;
    }

    // Reject transcripts under the minimum word count before we stamp the
    // document as "processing". No point submitting a sliver to the AI pipeline,
    // and the user's mental model is "the note didn't happen" — so we also
    // soft-delete the partial-transcript document immediately rather than
    // leaving an error-status row in their library.
    const wordCount = transcript.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < minimumTranscriptWordCount) {
      showNotification("Not enough text to process this note.", "error");
      setCurrentStatus("Not enough text captured");
      setProcessingFailed(true);

      const pid = recordingPatientIdRef.current || patientId;
      const docId = documentIdRef.current;
      const { removePendingJob } = activeJobsRef.current;
      try {
        await updateData(`patients/${pid}/documents`, docId, {
          status: "error",
          errorMessage: "Not enough text captured to process.",
          isArchived: true,
          modifiedAt: new Date(),
        });
        logAuditEventAsync(AuditActions.DOCUMENT_ARCHIVE, {
          resourceType: "document",
          resourceId: docId,
          patientId: pid,
          metadata: {
            reason: "insufficient_transcript_word_count",
            wordCount,
            minimumWordCount: minimumTranscriptWordCount,
            mode: "prerecorded",
          },
        });
      } catch (err) {
        console.error("[useRecordingSession] Failed to archive short-transcript doc:", err);
      }
      removePendingJob(docId);
      isProcessingRef.current = false;
      if (setIsProcessing) setIsProcessing(false);
      return;
    }

    setCurrentStatus("Submitting for processing...");
    setRetryStatus("");
    // Bridge AppShell's isProcessing immediately so the top-bar indicator
    // stays visible across the gap between handoff start and the first
    // Firestore onSnapshot update. Cleared via onComplete/onError in AppShell.
    if (setIsProcessing) setIsProcessing(true);

    // removePendingJob is used for error handling (addPendingJob is called earlier in stop())
    const { removePendingJob } = activeJobsRef.current;

    // Use pinned patientId from recording start (stable across navigation)
    const pid = recordingPatientIdRef.current || patientId;

    // Update doc status to processing + write quality data (Phase 4)
    try {
      const docUpdate = {
        status: "processing",
        modifiedAt: new Date(),
      };

      // Persist quality label + note on the document record
      if (qualityData) {
        docUpdate.audioQualityLabel = qualityData.label;
        const note = buildQualityNote(qualityData.label, qualityData.summary);
        if (note) docUpdate.audioQualityNote = note;
        if (qualityData.userOverride) docUpdate.audioQualityUserOverride = true;
      }

      // This transition must finish before submission. A debounced write can
      // arrive after a fast job completes and overwrite its completed status.
      await updateData(`patients/${pid}/documents`, documentIdRef.current, docUpdate);

      // Write detailed quality data to transcriptionData/quality subcollection
      if (qualityData?.summary) {
        try {
          await setData(`patients/${pid}/documents/${documentIdRef.current}/transcriptionData`, "quality", {
            ...qualityData.summary,
            qualityLabel: qualityData.label,
            userOverride: qualityData.userOverride || false,
            assessedAt: new Date(),
            source: "prerecorded",
            userId,
            chunkQcDetails: chunkQcResultsRef.current.filter(Boolean),
          });
        } catch (qErr) {
          console.error("[useRecordingSession] Failed to write quality data:", qErr);
          // Non-fatal — processing continues
        }
      }
    } catch (error) {
      console.error("[useRecordingSession] Failed to update doc status:", error);
    }

    // Submit the same atomic context pinned when recording started. Never fall
    // back to current route state here: the clinician may have navigated to a
    // different patient while transcription/quality checks were finishing.
    const pinnedPatientData = recordingPatientDataRef.current;
    const pinnedPatientDocuments = recordingPatientDocumentsRef.current;
    if (!pid || !pinnedPatientData || pinnedPatientData.id !== pid) {
      console.error("[useRecordingSession] Refusing processing handoff with mismatched patient context");
      showNotification("Patient context changed before processing. The note was not submitted.", "error", 8000, {
        key: "patient-context-mismatch",
        priority: true,
      });
      setCurrentStatus("Patient context changed");
      setProcessingFailed(true);
      removePendingJob(documentIdRef.current);
      isProcessingRef.current = false;
      if (setIsProcessing) setIsProcessing(false);
      return;
    }

    const result = await submitForProcessing({
      text: transcript.trim(),
      documentTypeId: selectedDocumentTypeId,
      patientData: pinnedPatientData,
      patientDocuments: pinnedPatientDocuments || [],
      documentId: documentIdRef.current,
      patientId: pid,
      streamSessionId: sessId,
      source: "recording",
    });

    if (result.success) {
      setCurrentStatus(result.status === "queued" ? "Queued for processing" : "Processing...");
      isProcessingRef.current = false;
    } else {
      console.error("[useRecordingSession] Processing failed:", result.error);
      setCurrentStatus(result.error || "Processing failed");
      setProcessingFailed(true);
      removePendingJob(documentIdRef.current);

      // Update document status to error so it doesn't remain stuck in "processing"
      try {
        await updateData(`patients/${pid}/documents`, documentIdRef.current, {
          status: "error",
          errorMessage: result.error || "Processing failed",
          modifiedAt: new Date(),
        });
      } catch (statusErr) {
        console.error("[useRecordingSession] Failed to update doc status to error:", statusErr);
      }

      isProcessingRef.current = false;
    }
  }, [patientId, selectedDocumentTypeId, userId, setIsProcessing]);
  // Keep stable ref in sync so runQualityGate / processAnyway (defined earlier) can call through it
  handoffRef.current = handleProcessingHandoff;

  // ── Effects ──────────────────────────────────────────────

  // Fetch patient data and setup on mount / patientId change
  useEffect(() => {
    let active = true;

    loadedPatientContextRef.current = null;
    setLoadedPatientId(null);
    setIsReady(false);
    setPatientData({});
    setPatientDocuments([]);
    setShareInfo(null);
    setDocumentTypes([]);

    if (!patientId) {
      return () => {
        active = false;
      };
    }

    const fetchData = async () => {
      const patientResult = await fetchPatientData(patientId);
      if (!active || !patientResult) return;

      // Fetch share info
      let nextShareInfo = null;
      if (userId) {
        try {
          nextShareInfo = await getActiveShareForUser(userId, patientId);
        } catch {
          nextShareInfo = null;
        }
      }
      if (!active) return;

      let settings = null;
      let companyId = null;
      if (userId) {
        try {
          const [fetchedSettings, userDoc] = await Promise.all([
            getUserSettings(userId),
            getDocument('users', userId)
          ]);
          settings = fetchedSettings;
          companyId = userDoc?.companyId || null;
        } catch {
          // ignore
        }
      }

      const ifActive = setter => value => { if (active) setter(value); };
      await fetchDocumentTypes(
        ifActive(setDocumentTypes),
        ifActive(setSelectedDocumentType),
        ifActive(setSelectedDocumentTypeId),
        ifActive(setSelectedCategoryId),
        ifActive(setEta),
        0,
        settings,
        companyId
      );
      if (!active) return;

      const canonicalPatientData = {
        ...patientResult.patientData,
        id: patientId,
      };
      const patientContext = {
        patientId,
        patientData: canonicalPatientData,
        patientDocuments: patientResult.patientDocuments || [],
      };
      loadedPatientContextRef.current = patientContext;
      setPatientData(canonicalPatientData);
      setPatientDocuments(patientContext.patientDocuments);
      setShareInfo(nextShareInfo);
      setLoadedPatientId(patientId);

      // Request mic permission early
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          stream.getTracks().forEach((t) => t.stop());
          fetchAudioInputDevices(setAudioDevices, setSelectedDeviceId);
        })
        .catch(() => {
          fetchAudioInputDevices(setAudioDevices, setSelectedDeviceId);
        });

      setIsReady(true);
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [patientId, userId]);

  // Sync patient data updates from other components
  useEffect(() => {
    const handlePatientUpdate = (event) => {
      const { patientId: updatedId, patientData: updatedData } = event.detail || {};
      if (updatedId === patientId) {
        const canonicalPatientData = { ...updatedData, id: updatedId };
        setPatientData(canonicalPatientData);
        if (loadedPatientContextRef.current?.patientId === updatedId) {
          loadedPatientContextRef.current = {
            ...loadedPatientContextRef.current,
            patientData: canonicalPatientData,
          };
        }
      }
    };
    window.addEventListener("patientUpdated", handlePatientUpdate);
    return () => window.removeEventListener("patientUpdated", handlePatientUpdate);
  }, [patientId]);

  // Re-fetch document types when the user prunes their list via "Manage Documents…"
  useEffect(() => {
    let active = true;
    let requestVersion = 0;
    const handleDocTypesUpdated = async () => {
      if (!userId) return;
      const version = ++requestVersion;
      setDocumentTypes([]);
      try {
        const [settings, userDoc] = await Promise.all([
          getUserSettings(userId),
          getDocument('users', userId),
        ]);
        const companyId = userDoc?.companyId || null;
        await fetchDocumentTypes(
          types => { if (active && version === requestVersion) setDocumentTypes(types); },
          () => {},
          () => {},
          () => {},
          () => {},
          null,
          settings,
          companyId
        );
      } catch (err) {
        console.error('[useRecordingSession] Failed to refresh document types', err);
      }
    };
    window.addEventListener('documentTypesUpdated', handleDocTypesUpdated);
    return () => { active = false; window.removeEventListener('documentTypesUpdated', handleDocTypesUpdated); };
  }, [userId]);

  // Device change listener
  useEffect(() => {
    const handleDeviceChange = () => {
      fetchAudioInputDevices(setAudioDevices, setSelectedDeviceId);
    };
    navigator.mediaDevices?.addEventListener("devicechange", handleDeviceChange);
    return () => navigator.mediaDevices?.removeEventListener("devicechange", handleDeviceChange);
  }, []);

  // Apply saved user settings for mic/doc type
  useEffect(() => {
    if (!patientId || !userId || documentTypes.length === 0) {
      setSelectedDocumentTypeId('');
      setSelectedDocumentType('');
      setSelectedCategoryId('');
      setEta(null);
      return;
    }
    let active = true;

    const fetchSettings = async () => {
      try {
        const settings = await getUserSettings(userId);
        if (!active) return;
        if (settings) {
          if (settings.microphone) setSelectedDeviceId(settings.microphone);

          // Pick a valid document type in priority order:
          // saved default -> current valid selection -> first visible option.
          const savedMatch = settings.documentType
            ? documentTypes.find((t) => t.id === settings.documentType)
            : null;
          const currentMatch = selectedDocumentTypeId
            ? documentTypes.find((t) => t.id === selectedDocumentTypeId)
            : null;
          const fallback = documentTypes[0] || null;
          const resolved = savedMatch || currentMatch || fallback;

          if (resolved) {
            setSelectedDocumentTypeId(resolved.id || "");
            setSelectedDocumentType(resolved.name || "");
            setSelectedCategoryId(resolved.categoryId || resolved.category || "");
            setEta(resolved.eta || 0);

            // Policy can temporarily hide a saved choice. Preserve it.
          }
        }
      } catch {
        // ignore
      }
    };
    fetchSettings();
    return () => { active = false; };
  }, [patientId, userId, documentTypes, selectedDocumentTypeId]);

  // Recording timer (drift-corrected, pause-aware)
  useEffect(() => {
    const clearTick = () => {
      if (recordingTickRef.current) {
        clearTimeout(recordingTickRef.current);
        recordingTickRef.current = null;
      }
    };

    if (!isStreaming) {
      clearTick();
      recordingStartRef.current = null;
      pausedAtRef.current = null;
      setRecordingTime(0);
      return;
    }

    if (!recordingStartRef.current) {
      recordingStartRef.current = Date.now();
    }

    // When pausing: record the pause timestamp and freeze the timer
    if (isPaused) {
      clearTick();
      if (!pausedAtRef.current) {
        pausedAtRef.current = Date.now();
      }
      return;
    }

    // When resuming: shift the start time forward by the paused duration
    if (pausedAtRef.current) {
      const pausedMs = Date.now() - pausedAtRef.current;
      recordingStartRef.current += pausedMs;
      pausedAtRef.current = null;
    }

    const tick = () => {
      const elapsedSec = Math.floor((Date.now() - recordingStartRef.current) / 1000);
      setRecordingTime(elapsedSec);

      if (elapsedSec >= recordingTimeLimit) {
        console.log(`[useRecordingSession] Time limit reached: ${(elapsedSec / 60).toFixed(1)}min`);
        stop();
        return clearTick();
      }

      if (elapsedSec > 0 && elapsedSec % 300 === 0) {
        console.log(`[useRecordingSession] Recording progress: ${(elapsedSec / 60).toFixed(1)}min`);
      }

      const nextDelay = Math.max(250, recordingStartRef.current + (elapsedSec + 1) * 1000 - Date.now());
      recordingTickRef.current = setTimeout(tick, nextDelay);
    };

    tick();
    return clearTick;
  }, [isStreaming, isPaused]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety cleanup: ensure volume polling and meter are stopped when recording ends
  // Catches edge cases where stop/cancel cleanup misses the interval (e.g., error paths)
  useEffect(() => {
    if (!isStreaming) {
      if (volumePollRef.current) {
        clearInterval(volumePollRef.current);
        volumePollRef.current = null;
      }
      if (volumeMeterRef.current?.cleanup) {
        volumeMeterRef.current.cleanup();
        volumeMeterRef.current = null;
      }
      setAudioWarning("");
      setShowCancelConfirm(false); // Reset cancel confirmation state
      // Reset silence detection when recording stops
      silenceStartTimeRef.current = null;
      setLocalSilenceDetected(false);
      lastDotTimestampRef.current = 0;
      setAdaptiveSilenceThreshold(silenceThreshold);
      silenceCalibrationStartRef.current = null;
      maxCalibrationVolumeRef.current = 0;
      silenceThresholdLockedRef.current = false;
    }
  }, [isStreaming]);

  // Silence detection: track prolonged low volume (10s+) and add timeline dots
  const SILENCE_DETECTION_THRESHOLD = 10000; // 10s
  const SILENCE_CALIBRATION_WINDOW_MS = 8000; // Tune threshold in first 8s of active recording
  const DOT_MIN_INTERVAL = 60000; // 1 dot per minute max
  const MAX_TIMELINE_DOTS = 120; // Cap at 2 hours of recording

  // Adaptive silence threshold tuning for low-gain mics.
  // Keeps POC behavior simple: only relaxes threshold down when startup levels are low.
  useEffect(() => {
    if (!isStreaming || isPaused || silenceThresholdLockedRef.current || !silenceCalibrationStartRef.current) {
      return;
    }

    if (volumeLevel > maxCalibrationVolumeRef.current) {
      maxCalibrationVolumeRef.current = volumeLevel;
    }

    const elapsedMs = Date.now() - silenceCalibrationStartRef.current;
    if (elapsedMs < SILENCE_CALIBRATION_WINDOW_MS) {
      return;
    }

    let tunedThreshold = silenceThreshold;
    if (maxCalibrationVolumeRef.current <= 2) {
      tunedThreshold = 0;
    } else if (maxCalibrationVolumeRef.current <= 4) {
      tunedThreshold = Math.min(1, silenceThreshold);
    }

    silenceThresholdLockedRef.current = true;
    setAdaptiveSilenceThreshold(tunedThreshold);
    console.info("[useRecordingSession] Adaptive silence threshold calibrated", {
      defaultThreshold: silenceThreshold,
      tunedThreshold,
      maxStartupVolumeLevel: maxCalibrationVolumeRef.current,
      calibrationWindowMs: SILENCE_CALIBRATION_WINDOW_MS,
    });
  }, [isStreaming, isPaused, volumeLevel]);

  // Track silence start time based on volume changes
  useEffect(() => {
    if (!isStreaming || isPaused) {
      silenceStartTimeRef.current = null;
      return;
    }

    // Check volume level: 0-1 is considered silence
    if (volumeLevel <= adaptiveSilenceThreshold) {
      if (!silenceStartTimeRef.current) {
        silenceStartTimeRef.current = Date.now();
      }
    } else {
      // Volume is acceptable, reset tracking
      silenceStartTimeRef.current = null;
      if (localSilenceDetected) {
        setLocalSilenceDetected(false);
      }
    }
  }, [volumeLevel, isStreaming, isPaused, localSilenceDetected, adaptiveSilenceThreshold]);

  // Poll for silence threshold (volume effect only fires on changes, not elapsed time)
  useEffect(() => {
    if (!isStreaming || isPaused) {
      setLocalSilenceDetected(false);
      return;
    }

    const checkSilence = () => {
      if (silenceStartTimeRef.current) {
        const silenceDuration = Date.now() - silenceStartTimeRef.current;
        if (silenceDuration >= SILENCE_DETECTION_THRESHOLD) {
          if (!localSilenceDetected) {
            setLocalSilenceDetected(true);
            // Add a silence dot to timeline (if 60s since last dot)
            const now = Date.now();
            if (now - lastDotTimestampRef.current >= DOT_MIN_INTERVAL) {
              lastDotTimestampRef.current = now;
              setChunkStatusHistory(prev => {
                if (prev.length >= MAX_TIMELINE_DOTS) return prev;
                return [...prev, { qcResult: "silence", speakerCount: 0, timestamp: now }];
              });
            }
          }
        }
      }
    };

    // Check immediately and then every second
    checkSilence();
    const intervalId = setInterval(checkSilence, 1000);
    return () => clearInterval(intervalId);
  }, [isStreaming, isPaused, localSilenceDetected]);

  // Clean up prepared mic when navigating away during confirmation phase.
  // AppShell never unmounts, so patientId changing is the signal that the user left.
  useEffect(() => {
    return () => {
      // Only clean up if we're in the preparation phase (mic acquired, not yet recording).
      // Active recordings use a pinned patientId ref and should keep running.
      const isRecording = chunkRecorderRef.current || mediaRecorderRef.current;
      if (!isRecording && audioStreamRef.current) {
        console.log("[useRecordingSession] Releasing prepared mic — user navigated away");
        audioStreamRef.current.getTracks().forEach(t => t.stop());
        audioStreamRef.current = null;
      }
      if (!isRecording && volumeMeterRef.current) {
        volumeMeterRef.current.cleanup?.();
        volumeMeterRef.current = null;
      }
      if (!isRecording) {
        setShowConfirmation(false);
        setVolumeLevel(0);
        setIsStarting(false);
      }
    };
  }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety: release all audio resources on full unmount
  useEffect(() => {
    return () => {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(t => t.stop());
        audioStreamRef.current = null;
      }
      if (volumeMeterRef.current) {
        volumeMeterRef.current.cleanup?.();
        volumeMeterRef.current = null;
      }
      if (volumePollRef.current) {
        clearInterval(volumePollRef.current);
        volumePollRef.current = null;
      }
      if (micChangedTimerRef.current) {
        clearTimeout(micChangedTimerRef.current);
      }
    };
  }, []);

  // Wake lock management
  useEffect(() => {
    if (!("wakeLock" in navigator)) return;

    const manage = async () => {
      try {
        if (isStreaming && !wakeLockRef.current) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        } else if (!isStreaming && wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
        }
      } catch (err) {
        console.warn("[useRecordingSession] Wake lock error:", err);
      }
    };
    manage();

    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, [isStreaming]);

  // ── Return API ───────────────────────────────────────────
  return {
    // State
    patientData,
    patientDocuments,
    documentTypes,
    selectedDocumentType,
    selectedDocumentTypeId,
    selectedCategoryId,
    audioDevices,
    selectedDeviceId,
    recordingTime,
    volumeLevel,
    documentId,
    sessionId,
    shareInfo,
    showConfirmation,
    isStarting,
    showCancelDialog,
    processingFailed,
    currentStatus,
    retryStatus,
    isReady,
    isPaused,
    audioWarning,
    showQualityGate,
    qualityAssessment,
    chunkStatusHistory,
    localSilenceDetected,
    recordingStatus: computeRecordingStatus(chunkStatusHistory),

    // Actions
    requestStart,
    confirmStart,
    cancelConfirmation,
    stop,
    requestCancel,
    confirmCancel,
    dismissCancelDialog,
    retryProcessing,
    selectDocumentType,
    selectDevice,
    swapMicDuringRecording,
    pauseRecording,
    resumeRecording,
    processAnyway,
    discardRecording,
    cancelRecording,
    setCancelConfirm,
    showCancelConfirm,
    micJustChanged,

    // Refs
    documentIdRef,
    chunkQcResultsRef,
    audioQualityMetricsRef,
  };
}
